// @ts-check
// =============================================================================
// js/screens/duel/animations.js — the duel-update queue + GSAP choreography.
//
// THE MODEL (plan/duel.md "Animation model")
//   The server ships FULL duel states, not deltas. Each `Duel Update` carries
//   hints about what produced it: `duel.history` (the action string), the new
//   `duel.log` line, `duel.justHatched`. We:
//
//     1. queue incoming updates for the SAME duel (duelQueue),
//     2. inspect the hints to pick a GSAP timeline (attack lunge, event
//        fly-in) parameterized by the action's spots,
//     3. play timelines sequentially with input locked,
//     4. then commit the queued state to the store and re-render.
//
//   FALLBACKS (animation must never block correctness): an update for a
//   DIFFERENT duel, a reconnect replay, or an unrecognized pattern skips
//   animation and renders the final state directly.
//
// ALSO OWNED HERE (they ride the same update stream, old SetupSocket :6715):
//   - sound cues derived from the log line (app/audio.js),
//   - the log append/reset,
//   - challenge-ladder progression on bot wins (app/progression.js),
//   - the single-player "bot takes its turn" trigger (Phase 7 registers the
//     real bot runner through setBotTurnHandler).
// =============================================================================

import * as PIXI from 'pixi.js';
import gsap from 'gsap';
import { store, setSlice } from '../../core/store.js';
import { playSound, soundsForLogLine } from '../../app/audio.js';
import { recordBotResult } from '../../app/progression.js';
import { targeting, resetTargeting, serverReplied, unlockInput } from './targeting.js';
import { appendLog, clearLog } from './log.js';
import { fanLayout } from './hand.js';
import { preloadOwnDuelArts } from '../../render/art-preload.js';

// A tween frozen by a hidden tab (rAF stops) must NOT resume from where it
// paused when the player returns — that reads as the animation firing again.
// With lag smoothing off, GSAP's clock keeps real time: on return the frozen
// timeline jumps straight to done and its completion callbacks fire in order.
gsap.ticker.lagSmoothing(0);

/** Queued full-state updates awaiting their turn to display. */
let duelQueue = [];

/** The scene being animated (null while the duel screen is unmounted). */
let scene = null;

/** Phase 7 plugs the bot AI in here: (duel) => void. */
let botTurnHandler = /** @type {((duel: any) => void)|null} */ (null);

/**
 * Register the bot-turn trigger (net/bot-runner.js).
 * @param {((duel: any) => void)|null} handler
 */
export function setBotTurnHandler(handler) {
	botTurnHandler = handler;
}

/**
 * The duel screen hands us its scene on mount (null on unmount).
 * @param {any} activeScene
 */
export function setAnimationScene(activeScene) {
	releaseLingeringEvent();
	scene = activeScene;
	duelQueue = [];
	targeting.locked = false;
}

// -----------------------------------------------------------------------------
// The lingering event card: after an event flies to its target it stays there
// a while (so you can read what hit you) — unless another action comes in,
// which cuts it short with a fast fade.
// -----------------------------------------------------------------------------

/** The event sprite currently lingering on its target (at most one). */
let lingeringEvent = null;

/** How long an arrived event card sits on its target before fading. Tunables:
 *  HOLD = the still, fully-visible dwell; FADE = the ease-out after it.
 *  (Total on-screen ≈ 0.5s flight + HOLD + FADE; a new action cuts it short.) */
const EVENT_LINGER_HOLD = 1.1;
const EVENT_LINGER_FADE = 0.35;

/**
 * Park an arrived event sprite on its target: hold, then fade out.
 * @param {any} sprite
 */
function lingerEventSprite(sprite) {
	releaseLingeringEvent(true);
	lingeringEvent = sprite;
	sprite.__linger = gsap.timeline()
		.to(sprite, { alpha: 0, duration: EVENT_LINGER_FADE, ease: 'power1.in', delay: EVENT_LINGER_HOLD })
		.call(() => {
			if (lingeringEvent === sprite) lingeringEvent = null;
			if (!sprite.destroyed) sprite.destroy({ children: true });
		});
}

/**
 * Remove the lingering event sprite (no-op when none).
 * @param {boolean} [fast]  true = quick fade (a new action arrived);
 *                          false = destroy immediately (scene going away).
 */
function releaseLingeringEvent(fast = false) {
	const sprite = lingeringEvent;
	if (!sprite) return;
	lingeringEvent = null;
	sprite.__linger?.kill();
	if (sprite.destroyed) return;
	if (fast) {
		gsap.to(sprite, {
			alpha: 0, duration: 0.15, ease: 'none',
			onComplete: () => { if (!sprite.destroyed) sprite.destroy({ children: true }); },
		});
	} else {
		sprite.destroy({ children: true });
	}
}

/**
 * A compact signature of the MEANINGFUL duel state (board contents + both
 * players' resources / turn / winner). Two updates with the same signature are
 * the same moment in the game, so a second one is a re-send, not a new move.
 * Malformed input returns a unique value so it never falsely matches.
 * @param {any} d
 * @returns {string}
 */
export function stateSig(d) {
	if (!d || !d.player1 || !d.player2 || !Array.isArray(d.board)) return 'x' + Math.random();
	const pl = (p) => [
		p.tick, p.deck, typeof p.hand === 'number' ? 'n' + p.hand : 'a' + (p.hand?.length ?? 0),
		p.crop, p.meat, p.turn, p.actionsSpent, p.winner ? 'W' : '', (p.play || []).length, (p.dead || []).length,
	].join(',');
	const board = d.board.map((row) => (row || []).map((c) => (c
		? [c.id, c.attack, c.health, c.wait ?? '', c.ready ? 1 : 0, c.alive ? 1 : 0].join(':')
		: '_')).join(',')).join(';');
	return `${pl(d.player1)}#${pl(d.player2)}#${board}#${d.GameOver ? 1 : 0}${d.GameClose ? 1 : 0}`;
}

/**
 * THE socket 'Duel Update' handler while the duel system is live (registered
 * via net/socket.setDuelUpdateHandler by the duel screen; also called for
 * reconnect replays).
 * @param {any} newDuel  The fresh viewer-stripped duel state.
 * @returns {void}
 */
export function onDuelUpdate(newDuel) {
	const current = store.duel;
	const sameDuel = current && current.id === newDuel.id;

	// The server answered: disarm the send-lock failsafe. The lock itself
	// releases when this update finishes displaying (queue drain / commit).
	serverReplied();

	// The server's final GameOver/GameClose push (sent right before it deletes
	// the duel) can arrive after we've already left and cleared the store —
	// committing it would resurrect a dead game we can't act in. Ended duels
	// only matter for the duel we're actually holding.
	if ((newDuel.GameOver || newDuel.GameClose) && !sameDuel) return;

	// RE-SEND of the state we already hold: on reconnect / tab-refocus the server
	// re-pushes the current duel — often WITH its last `history` — but that move
	// already happened. Without this guard, animateAction would replay the last
	// action (e.g. the devour swoop that eats the loser's leader) EVERY time the
	// tab regains focus, and re-fire its log/sound/progression. Same state in ->
	// commit it silently, no re-animation.
	const isResend = sameDuel && stateSig(newDuel) === stateSig(current);

	if (sameDuel && !isResend) {
		// Progression check must see the WINNING update (old :6717).
		if (newDuel.player1?.winner) {
			recordBotResult(newDuel);
		}
		// Sound cues + log line ride every update for the visible duel.
		// No cues while hidden: an update that lands in a background tab must
		// not schedule sounds for the player's return (playSound also hard-
		// gates at fire time — this stops even the bookkeeping).
		if (newDuel.log && !current.GameClose) {
			if (!document.hidden) {
				for (const cue of soundsForLogLine(newDuel.log, newDuel)) {
					setTimeout(() => playSound(cue.name), cue.delayMs);
				}
			}
			appendLog(newDuel.log);
		}
	} else if (!sameDuel) {
		clearLog();
	}

	// HIDDEN TAB: never queue animations — commit instantly. Timelines can't
	// tick without rAF, so a queued animation would sit frozen and visibly
	// (re)play the moment the player came back. They return to current state.
	// RE-SEND: same deal — the move already displayed, so don't re-animate it.
	if (newDuel.history && sameDuel && scene && !document.hidden && !isResend) {
		// Animate from the current state into this one.
		duelQueue.push(newDuel);
		if (duelQueue.length === 1) {
			animateAction(newDuel.history);
		}
	} else {
		// New duel / reconnect / no screen: skip animation, render final state.
		releaseLingeringEvent();
		duelQueue = [];
		commitDuel(newDuel);
		unlockInput(); // no animation queue to release the send-lock — do it here
		// A fresh game while the screen is already mounted skips mountScene's
		// art warm-up — run it here (dedup makes reconnect replays free).
		void preloadOwnDuelArts(newDuel, store.loadedDeck);
	}
}

/**
 * Choose + play the timeline for one queued update (old AnimateAction :4511).
 * @param {{action?: string, event?: any}} history
 */
function animateAction(history) {
	targeting.locked = true;
	// A new action arrived — the previous event card stops lingering.
	releaseLingeringEvent(true);
	try {
		const action = String(history?.action || '');
		const parts = action.split('__');

		if (history?.event && scene) {
			// An event card resolving: fly the card itself to the target.
			playEventFly(history.event, parts[1] || '', parts[2] || '', () => advanceQueue());
			return;
		}
		if (parts[0] === 'Attack' && scene) {
			playAttackLunge(parts[1], parts[2], () => advanceQueue());
			return;
		}
		if (parts[0] === 'Play' && String(parts[2] || '').startsWith('Board') && scene) {
			// A CHARGE creature (Plump Pouncer &c.) attacks the instant it
			// lands — the engine reports its auto-attack under history.charge.
			// Chain the card flight into the lunge/devour, not a plain placement.
			if (history?.charge) {
				playChargeCreature(parts[1], parts[2], String(history.charge), () => advanceQueue());
				return;
			}
			// A creature/egg leaving the hand for a board slot.
			playCardFlight(parts[1], parts[2], () => advanceQueue());
			return;
		}
		if (parts[0] === 'Devour' && scene) {
			// The devouring player's leader swoops in and eats the target.
			playDevour(parts[1], () => advanceQueue());
			return;
		}
	} catch (error) {
		console.error('animateAction failed (rendering state directly):', error);
	}
	// Draw/Crop/Devour and anything unrecognized: no timeline, just show it.
	advanceQueue();
}

/** Shift the queue: commit the next state; keep animating if more queued. */
function advanceQueue() {
	const next = duelQueue.shift();
	if (next) commitDuel(next);
	if (duelQueue.length) {
		setTimeout(() => animateAction(duelQueue[0].history), 250);
	} else {
		unlockInput(); // queue drained: the send-lock releases with it
	}
}

/**
 * Make a duel state current: store it, reset targeting, refresh the scene,
 * handle game-over, and poke the bot when it's the bot's move (old
 * PushNextDuel :4484).
 * @param {any} duel
 */
function commitDuel(duel) {
	setSlice('duel', duel);
	resetTargeting();

	// Decided? An in-game win (devour / alt-win) sets the winner flags
	// immediately; GameOver only arrives on the final teardown push (Leave /
	// reaper). Either way: show the result, stop poking the bot.
	if (duel.GameOver || duel.player1?.winner || duel.player2?.winner) {
		clearLogSoon();
		scene?.showGameOver(duel);
		scene?.refresh();
		return;
	}
	scene?.refresh();
	kickBotIfNeeded();
}

/**
 * Single-player: if it's the bot's move, poke the bot runner. Called after
 * every committed state AND by the duel screen on mount — a Bot duel can
 * START on the bot's turn, and the initial state arrives via the Bot Game
 * response, not a socket update. Pacing lives in the runner: onBotTurn adds
 * a randomized ~1–2s "thinking" beat on top of the scripts' own SendAction
 * delay, so repeated kicks here are safely absorbed by its pending-timer.
 * @returns {void}
 */
export function kickBotIfNeeded() {
	const duel = store.duel;
	// Never on a SPECTATOR client: only the player's client runs the bot (a
	// spectator's state has the bot's hand stripped, so it can't act anyway).
	if (duel && duel.format === 'Bot' && !duel.spectating
		&& duel.player1.tick === 0 && !duel.GameOver
		&& duelQueue.length === 0 && botTurnHandler) {
		botTurnHandler(duel);
	}
}

/** Reset the log a beat after game over so the final lines stay readable. */
function clearLogSoon() {
	setTimeout(() => {
		if (!store.duel || store.duel.GameOver) clearLog();
	}, 4000);
}

// -----------------------------------------------------------------------------
// Timelines
// -----------------------------------------------------------------------------

/**
 * Attack lunge (old PlayAttack :4555): dart the attacker's tile at its
 * victim, land the IMPACT (flash + shake + damage number, and a screen
 * shake for heavy hits), and snap back. A RAVENGER kill doesn't snap back —
 * the attacker moves onto its victim and EATS it (the victim shrinks away
 * beneath it). Spots in `history.action` are from the ACTOR's perspective —
 * when the opponent acted (their tick is live in OUR current state), mirror
 * the rows (3 - y).
 * @param {string} attackerSpot  "Board_y_x"
 * @param {string} targetSpot    "Board_y_x" | "…Opponent…"
 * @param {() => void} done
 */
function playAttackLunge(attackerSpot, targetSpot, done) {
	const duel = store.duel;
	const flip = !!duel?.player2?.tick; // opponent acting -> mirror rows
	const tile = tileAtSpot(attackerSpot, flip);
	const attackerCell = cellAtSpot(attackerSpot, flip);
	if (!tile) return done();
	const attacker = attackerCell?.data;
	const damage = Math.max(0, Number(attacker?.attack || 0) + Number(attacker?.static?.extraAttack || 0));

	const faceHit = String(targetSpot).includes('Opponent');
	const victimCell = faceHit ? null : cellAtSpot(targetSpot, flip);
	// The face under attack in OUR view: their portrait when we attack,
	// ours when they do.
	const facePortrait = scene.barRefs?.portraits?.[flip ? 'Yourself' : 'Opponent'];

	let toX;
	let toY;
	if (faceHit) {
		// Face hit: lunge off the appropriate edge of the board.
		toX = tile.x;
		toY = flip ? scene.metrics.boardY + scene.metrics.tileH * 4 : scene.metrics.boardY - scene.metrics.tileH;
	} else {
		const victim = tileAtSpot(targetSpot, flip);
		if (!victim) return done();
		toX = victim.x;
		toY = victim.y;
	}

	const impact = () => {
		if (victimCell) {
			flashTile(victimCell);
			shakeObject(victimCell.wrap);
			floatDamage(victimCell.cellX + victimCell.w / 2, victimCell.cellY + victimCell.h * 0.3, damage);
		} else if (facePortrait && !facePortrait.container.destroyed) {
			// Direct hit on a leader: the portrait RECOILS — knocked away from
			// the board (top portrait up, bottom portrait down), tilted by the
			// attacker's column, springing back. `flip` says whose face this
			// is: ours sits on the bottom bar. NO damage number here — the deck
			// chip's red "−N" (bars.js announceDeckChange) is the one readout.
			knockbackPortrait(facePortrait, flip ? 1 : -1, damage, fromX + scene.metrics.tileW / 2);
		}
		if (damage > 4) screenShake(damage);
	};

	// RAVENGER kill: the victim's slot empties in the pending state (engine
	// actions.js — Ravager removes the victim; it does NOT move the attacker).
	// The attacker lunges onto the victim, chews it away, THEN returns to its
	// own slot. Earlier it stayed parked on the victim's tile with no return —
	// and since the attacker keeps its slot (and its render key), the reconcile
	// left the tile displaced there until a refresh re-derived positions.
	const pendingCell = victimCell && duelQueue[0]?.board?.[victimCell.y]?.[victimCell.x];
	const victimGone = victimCell && (!pendingCell || pendingCell.owner !== victimCell.data?.owner);
	const ravenous = !!attacker?.keywords?.ravenger && victimCell && !!victimCell.wrap && victimGone;

	// Keep the attacker ON TOP for the WHOLE lunge — out AND pull-back. The board
	// layer sits BELOW topBar/bottomBar/hand (see the layer order in duel.js), so
	// a face lunge would otherwise slide UNDER the leader portrait and a
	// board-to-board lunge can clip the bars. Reparent the tile to fx (the top
	// layer) for the duration — every layer shares the stage origin, so its
	// on-screen position is unchanged — then drop it back into the board, at its
	// original stacking, the instant the lunge finishes.
	const homeParent = tile.parent;
	const homeIndex = homeParent ? homeParent.getChildIndex(tile) : 0;
	scene.layers.fx.addChild(tile);
	const settle = () => {
		if (tile && !tile.destroyed && homeParent && !homeParent.destroyed) {
			tile.zIndex = 0;
			homeParent.addChildAt(tile, Math.min(homeIndex, homeParent.children.length));
		}
		done();
	};
	const fromX = tile.x;
	const fromY = tile.y;
	if (ravenous) {
		gsap.timeline({ onComplete: settle })
			.to(tile, { x: toX, y: toY, duration: 0.26, ease: 'power2.in' })
			.call(() => {
				impact();
				shrinkTileAway(victimCell, 0.24);
			})
			.to({}, { duration: 0.34 }) // a beat to chew…
			.to(tile, { x: fromX, y: fromY, duration: 0.3, ease: 'power2.out' }); // …then home
	} else {
		gsap.timeline({ onComplete: settle })
			.to(tile, { x: toX, y: toY, duration: 0.2, ease: 'power2.in' })
			.call(impact)
			.to(tile, { x: fromX, y: fromY, duration: 0.3, ease: 'power2.out' });
	}
}

/**
 * The Devour action: the devouring player's leader portrait swoops from
 * their bar onto the target, CHOMPS (the target shrinks away — no fade),
 * and swoops home. While the flier is out the REAL portrait hides — the
 * leader must never be on screen twice.
 *
 * A LEADER target (the winning devour) flies to the other player's portrait
 * instead: the chomp leaves the eaten leader's portrait BLANK in defeat
 * (the dark backing stays; the face is gone).
 * @param {string} targetSpot  "Board_y_x", or "Opponent"/"Yourself".
 * @param {() => void} done
 */
function playDevour(targetSpot, done) {
	let finished = false;
	let restorePortrait = () => {};
	const finish = () => {
		if (!finished) {
			finished = true;
			restorePortrait();
			done();
		}
	};
	try {
		const duel = store.duel;
		const flip = !!duel?.player2?.tick; // who devoured?
		const portraitRef = scene.barRefs?.portraits?.[flip ? 'Opponent' : 'Yourself'];
		const spot = String(targetSpot);
		const victimCell = spot.startsWith('Board') ? cellAtSpot(spot, flip) : null;
		// The winning devour targets the other LEADER ("Opponent" in the
		// actor's perspective — mirror it for the watching player).
		const victimPortrait = !victimCell && (spot.includes('Opponent') || spot.includes('Yourself'))
			? scene.barRefs?.portraits?.[flip ? 'Yourself' : 'Opponent']
			: null;
		if (!portraitRef || portraitRef.container.destroyed || (!victimCell?.wrap && !victimPortrait)) {
			finish();
			return;
		}
		setTimeout(finish, 1600); // the chomp must never stall the game

		const flier = makePortraitFlier(portraitRef);
		// The flier IS the leader for the duration.
		portraitRef.container.visible = false;
		restorePortrait = () => {
			if (portraitRef.container && !portraitRef.container.destroyed) {
				portraitRef.container.visible = true;
			}
		};
		const home = { x: flier.x, y: flier.y };
		let toX;
		let toY;
		if (victimCell) {
			toX = victimCell.cellX + victimCell.w / 2;
			toY = victimCell.cellY + victimCell.h / 2;
		} else {
			const g = victimPortrait.container.getGlobalPosition();
			toX = g.x + victimPortrait.w / 2;
			toY = g.y + victimPortrait.h / 2;
		}
		const chomp = victimPortrait ? 1.45 : 1.3; // eating a LEADER earns a bigger bite
		gsap.timeline()
			.to(flier, { x: toX, y: toY, duration: 0.3, ease: 'power2.in' })
			.call(() => {
				if (victimCell) {
					shrinkTileAway(victimCell, 0.22);
				} else if (victimPortrait.sprite && !victimPortrait.sprite.destroyed) {
					// Blank in defeat: the face is eaten, the dark socket remains.
					victimPortrait.sprite.visible = false;
				}
			})
			.to(flier.scale, { x: chomp, y: chomp, duration: 0.1, ease: 'none' }) // CHOMP
			.to(flier.scale, { x: 1, y: 1, duration: 0.12, ease: 'power1.out' })
			.to(flier, { x: home.x, y: home.y, duration: 0.3, ease: 'power2.out' })
			.call(() => {
				if (!flier.destroyed) flier.destroy({ children: true });
				finish();
			});
	} catch (error) {
		console.error('playDevour failed (rendering state directly):', error);
		finish();
	}
}

// -----------------------------------------------------------------------------
// Impact garnishes (attack feedback + shared eat/shrink pieces)
// -----------------------------------------------------------------------------

/** A red hit-flash over a board cell (drawn on fx, fades fast). */
function flashTile(cell) {
	const flash = new PIXI.Graphics()
		.roundRect(0, 0, cell.w, cell.h, Math.min(12, cell.w * 0.06))
		.fill({ color: 0xff4444, alpha: 0.5 });
	flash.x = cell.cellX;
	flash.y = cell.cellY;
	scene.layers.fx.addChild(flash);
	gsap.to(flash, { alpha: 0, duration: 0.3, ease: 'power1.out', onComplete: () => flash.destroy() });
}

/** Jitter a display object side-to-side and settle back where it was. */
/**
 * A leader portrait's hit reaction: jostled back off the board (away from
 * the battlefield) with the blow, TILTED with the blow's direction, then
 * springing home with a little wobble. Composes with shakeObject's x-jitter —
 * this owns y and the face-group rotation. Rest positions are remembered on
 * the ref so back-to-back hits can't drift the portrait.
 * @param {any} ref        scene.barRefs.portraits[spot] ({container,face,w,h,…}).
 * @param {1|-1} direction +1 = pushed down (bottom bar), −1 = up (top bar).
 * @param {number} damage  Scales the push a touch.
 * @param {number} [strikeX]  Where the blow CAME FROM (global x): a strike
 *   from the left of the portrait tips it clockwise, from the right counter-
 *   clockwise, dead-on not at all. Omit for no tilt.
 */
function knockbackPortrait(ref, direction, damage, strikeX) {
	const target = ref.container;
	if (!target || target.destroyed) return;
	const baseY = (ref.knockBaseY ??= target.y);
	const push = direction * Math.min(ref.h * 0.22, 10 + damage * 2);
	gsap.killTweensOf(target, 'y');
	gsap.timeline()
		.to(target, { y: baseY + push, duration: 0.08, ease: 'power2.out' })
		.to(target, { y: baseY, duration: 0.45, ease: 'elastic.out(1, 0.45)' });

	const face = ref.face;
	if (face && !face.destroyed && Number.isFinite(strikeX)) {
		const g = target.getGlobalPosition();
		const dx = (g.x + ref.w / 2) - strikeX; // + = struck from our left
		const halfBoard = ((scene?.metrics?.tileW || 120) * 4) / 2;
		const tilt = Math.max(-1, Math.min(1, dx / halfBoard)) * 0.18;
		gsap.killTweensOf(face);
		gsap.timeline()
			.to(face, { rotation: tilt, duration: 0.08, ease: 'power2.out' })
			.to(face, { rotation: 0, duration: 0.5, ease: 'elastic.out(1, 0.4)' });
	}
}

function shakeObject(obj, amp = 4) {
	if (!obj || obj.destroyed) return;
	const baseX = obj.x;
	gsap.timeline()
		.to(obj, { x: baseX - amp, duration: 0.04, ease: 'none' })
		.to(obj, { x: baseX + amp, duration: 0.05, ease: 'none' })
		.to(obj, { x: baseX - amp * 0.5, duration: 0.04, ease: 'none' })
		.to(obj, { x: baseX, duration: 0.05, ease: 'none' });
}

/** A floating "-N" damage number (skipped for 0 damage). */
function floatDamage(x, y, damage) {
	if (!damage) return;
	const text = new PIXI.Text({
		text: '-' + damage,
		style: {
			fontFamily: 'Luckiest Guy', fontSize: 26, fill: 0xff5b5b,
			stroke: { color: 0x000000, width: 4 },
		},
	});
	text.anchor.set(0.5);
	text.x = x;
	text.y = y;
	scene.layers.fx.addChild(text);
	gsap.timeline()
		.to(text, { y: y - 46, duration: 0.8, ease: 'power1.out' }, 0)
		.to(text.scale, { x: 1.25, y: 1.25, duration: 0.12, ease: 'none' }, 0)
		.to(text, { alpha: 0, duration: 0.3, ease: 'none' }, 0.5)
		.call(() => text.destroy());
}

/**
 * Big hits rattle the screen. Amplitude grows with the SQUARE ROOT of the
 * damage so a monster hit feels huge without launching the board offscreen.
 * Combat only fires it above 4 damage; board.js also borrows it for BIG
 * ENTRANCES (a creature arriving with 10+ health).
 * @param {number} damage
 */
export function screenShake(damage) {
	const stage = scene?.app?.stage;
	if (!stage) return;
	const amp = Math.min(24, 4 * Math.sqrt(damage));
	const timeline = gsap.timeline();
	for (let i = 0; i < 5; i++) {
		const falloff = 1 - i / 5;
		timeline.to(stage, {
			x: (Math.random() - 0.5) * 2 * amp * falloff,
			y: (Math.random() - 0.5) * 2 * amp * falloff,
			duration: 0.045,
			ease: 'none',
		});
	}
	timeline.to(stage, { x: 0, y: 0, duration: 0.06, ease: 'none' });
}

/**
 * A creature being eaten SHRINKS away (never fades) around its center.
 * The commit right after re-renders the slot from state either way.
 * @param {any} cell  A board cell whose wrap is being devoured.
 * @param {number} [duration]
 */
function shrinkTileAway(cell, duration = 0.22) {
	const wrap = cell?.wrap;
	if (!wrap || wrap.destroyed) return;
	wrap.pivot.set(cell.w / 2, cell.h / 2);
	wrap.x += cell.w / 2;
	wrap.y += cell.h / 2;
	gsap.to(wrap.scale, { x: 0, y: 0, duration, ease: 'power2.in' });
}

/**
 * A flying, rounded copy of a bar portrait (the real one stays put),
 * center-pivoted for chomp pulses. Caller destroys it.
 * @param {any} portraitRef  scene.barRefs.portraits[spot]
 * @returns {PIXI.Container}
 */
function makePortraitFlier(portraitRef) {
	const flier = new PIXI.Container();
	const sprite = new PIXI.Sprite(portraitRef.sprite.texture);
	sprite.width = portraitRef.w;
	sprite.height = portraitRef.h;
	const mask = new PIXI.Graphics()
		.roundRect(0, 0, portraitRef.w, portraitRef.h, portraitRef.r)
		.fill(0xffffff);
	flier.addChild(sprite, mask);
	sprite.mask = mask;
	flier.pivot.set(portraitRef.w / 2, portraitRef.h / 2);
	const g = portraitRef.container.getGlobalPosition();
	flier.x = g.x + portraitRef.w / 2;
	flier.y = g.y + portraitRef.h / 2;
	scene.layers.fx.addChild(flier);
	return flier;
}

/**
 * Event resolution (old WatchEvent :4656): the event card sails to its
 * target, lingers, and fades. For OUR plays the ACTUAL hand card flies (no
 * duplicate — it detaches from the hand, which re-fans behind it); for the
 * opponent's plays (hand hidden) a fresh render flies in from their side.
 * @param {{id: number}} eventCard   history.event (the resolved card).
 * @param {string} cardSpot          "Card_<i>" (the hand slot it left).
 * @param {string} targetSpot        "Yourself" | "Opponent" | "Board_y_x"
 * @param {() => void} done
 */
async function playEventFly(eventCard, cardSpot, targetSpot, done) {
	const duel = store.duel;
	const ourAction = !!duel?.player1?.tick;
	let finished = false;
	const finish = () => {
		if (!finished) {
			finished = true;
			done();
		}
	};
	// Whatever happens with art loading, never stall the game.
	setTimeout(finish, 2500);

	try {
		const { width, boardY, tileW, tileH, boardX } = scene.metrics;

		// OUR event: pull the real card out of the hand and fly IT (center-
		// pivoted so it can shrink toward the target as it travels).
		let sprite = null;
		let targetScale = 0;
		if (ourAction && String(cardSpot).startsWith('Card_')) {
			sprite = detachHandCardRender(Number(cardSpot.split('_')[1]));
			if (sprite) {
				const s = sprite.scale.x;
				sprite.pivot.set(778 / 2, 1082 / 2); // card native center
				sprite.x += (778 / 2) * s;
				sprite.y += (1082 / 2) * s;
				targetScale = (tileH * 1.5) / 1082; // same landing size as makeCardSprite
			}
		}
		if (!sprite) {
			sprite = await scene.makeCardSprite(store.cards[Number(eventCard.id)] || eventCard);
			sprite.x = width / 2;
			sprite.y = ourAction ? scene.metrics.handY : -sprite.height;
			scene.layers.fx.addChild(sprite);
		}

		// Where the event LAUNCHED from — feeds the struck leader's tilt.
		const startX = sprite.x;

		// Destination: the target tile, or the target player's bar.
		let toX = width / 2;
		let toY = boardY + tileH * 2;
		let victimCell = null;
		// A FACE-targeted event jostles the struck leader on arrival — but ONLY
		// a damage-themed one (Chomp, Bite The Deck and kin). The tell is the
		// struck player's DECK actually shrinking beyond any cards they drew
		// (same accounting as the bars' deck float); steals, buffs, and other
		// face plays arrive without the flinch.
		const faceSpot = String(targetSpot).includes('Yourself') ? 'Yourself'
			: String(targetSpot).includes('Opponent') ? 'Opponent' : null;
		const hitsOurFace = faceSpot ? (faceSpot === 'Yourself') === ourAction : false;
		let struckRef = null;
		if (faceSpot) {
			const struck = hitsOurFace ? 'player1' : 'player2';
			const before = duel?.[struck];
			const after = duelQueue[0]?.[struck];
			const handOf = (p) => (typeof p?.hand === 'number' ? p.hand : (p?.hand?.length ?? 0));
			const drawn = Math.max(0, handOf(after) - handOf(before));
			const deckBitten = !!before && !!after
				&& (Number(before.deck) - Number(after.deck)) > drawn;
			if (deckBitten) {
				struckRef = scene.barRefs?.portraits?.[hitsOurFace ? 'Yourself' : 'Opponent'];
			}
		}
		const spotParts = String(targetSpot).split('_');
		if (spotParts[0] === 'Board') {
			let y = Number(spotParts[1]);
			const x = Number(spotParts[2]);
			if (!ourAction) y = 3 - y; // mirror for the watching player
			toX = boardX + x * tileW + tileW / 2;
			toY = boardY + y * tileH + tileH / 2;
			// Did this event DEVOUR its target? (occupied now, gone in the
			// pending state) — then the target shrinks away under the card.
			const pendingCell = duelQueue[0]?.board?.[y]?.[x];
			if (duel?.board?.[y]?.[x] && !pendingCell) {
				victimCell = scene.boardCells?.[y]?.[x];
			}
		} else if ((targetSpot.includes('Yourself') && ourAction) || (targetSpot.includes('Opponent') && !ourAction)) {
			toY = scene.metrics.handY; // toward our side
		} else {
			// Targeting the opponent (top) bar — an event cast on the enemy, or the
			// enemy self-casting. Parking it near the top edge (toY≈20) tucked the
			// card under the bar / off-screen, so it "wasn't really visible". Sit it
			// JUST BELOW the enemy portrait instead: the whole card reads while most
			// of the leader portrait still shows above it.
			const portrait = scene.metrics.portraitTop;
			if (portrait) {
				toX = portrait.x;
				toY = portrait.y + scene.metrics.portraitD / 2 + (tileH * 1.5) / 2 + 6;
			} else {
				toY = 20;
			}
		}

		const flight = gsap.timeline()
			.to(sprite, { x: toX, y: toY, duration: 0.5, ease: 'power2.out' }, 0)
			.call(() => {
				// Arrived: the card stays parked on its target to be read (cut
				// short if another action comes in). An event-devour holds the
				// commit a beat while the victim shrinks under the card.
				lingerEventSprite(sprite);
				// DECK-BITING face events land like a blow: the struck leader
				// shakes and recoils (down for ours, up for theirs), tilted by
				// the flight's approach angle. struckRef is only set when the
				// event actually shrank their deck (see the gate above) — other
				// face plays arrive quietly.
				if (struckRef && !struckRef.container.destroyed) {
					shakeObject(struckRef.container, 5);
					knockbackPortrait(struckRef, hitsOurFace ? 1 : -1, 3, startX);
				}
				if (victimCell?.wrap) {
					shrinkTileAway(victimCell, 0.25);
					gsap.delayedCall(0.25, finish);
				} else {
					finish();
				}
			});
		if (targetScale) {
			flight.to(sprite.scale, { x: targetScale, y: targetScale, duration: 0.5, ease: 'power2.out' }, 0);
		}
	} catch (error) {
		console.error('playEventFly failed:', error);
		finish();
	}
}

/**
 * Lift a hand card's render out of its cell onto the fx layer, keeping its
 * on-screen transform, and re-fan the remaining cards behind it. The commit's
 * hand reconcile then destroys the emptied cell — or, when a duplicate copy
 * kept it, rebuilds the render (hand.js self-heal).
 * @param {number} index  Hand slot.
 * @returns {any|null}    The detached PixiCard container (top-left origin).
 */
function detachHandCardRender(index) {
	const entry = scene.handCells?.[index];
	const cardEl = entry && !entry.cell.destroyed ? entry.cell.children[1] : null;
	if (!cardEl) return null;
	const from = cardEl.getGlobalPosition();
	scene.layers.fx.addChild(cardEl);
	cardEl.position.set(from.x, from.y);
	entry.cell.visible = false;
	refanHandWithout(index);
	return cardEl;
}

/**
 * Close the hand's gap the moment a card lifts out — the remaining cards
 * tween to the layout they'll have once the play commits (purely visual;
 * the reconcile re-derives everything from state at commit).
 * @param {number} skipIndex
 */
function refanHandWithout(skipIndex) {
	const cells = (scene.handCells || []).filter(Boolean)
		.filter((entry) => entry.index !== skipIndex && entry.cell.visible);
	if (!cells.length || !scene.handMount) return;
	const { slotX } = fanLayout(scene, cells.length);
	cells.forEach((entry, i) => {
		entry.baseX = slotX(i);
		gsap.killTweensOf(entry.cell, 'x');
		gsap.to(entry.cell, { x: entry.baseX, duration: 0.25, ease: 'power2.out' });
	});
}

/**
 * A creature/egg played from OUR hand: the rendered card detaches from the
 * hand cell, flies to its board slot while shrinking, and lands with its ART
 * window exactly over the tile's ART window (card art: 48,148 682×512 within
 * 778×1082 · tile art: 22,22 635×476 within 683×515) — then the committed
 * tile appears underneath and the card crossfades away. Opponent plays skip
 * the flight (their hand is hidden; there is nothing to fly from).
 * @param {string} cardSpot    "Card_<i>"
 * @param {string} targetSpot  "Board_<y>_<x>"
 * @param {() => void} done
 */
function playCardFlight(cardSpot, targetSpot, done) {
	let finished = false;
	const finish = () => {
		if (!finished) {
			finished = true;
			done();
		}
	};
	try {
		const duel = store.duel;
		if (duel?.player2?.tick) {
			// The OPPONENT is playing: their hand is hidden, so a CARD BACK
			// flies from their portrait to the slot — the tile mounts under it.
			playEnemyCardFly(targetSpot, finish);
			return;
		}
		const index = Number(String(cardSpot).split('_')[1]);
		const spotParts = String(targetSpot).split('_');
		const target = scene.boardCells?.[Number(spotParts[1])]?.[Number(spotParts[2])];
		if (!target) {
			finish();
			return;
		}
		// Lift the real render out of the hand (the row re-fans behind it). It
		// may still be loading on a fresh hand — then there's nothing to fly.
		const cardEl = detachHandCardRender(index);
		if (!cardEl) {
			finish();
			return;
		}
		setTimeout(finish, 1600); // whatever the tween does, never stall the game

		// Where the tile's art window sits on screen.
		const plate = target.plate.getGlobalPosition();
		const artX = plate.x + target.w * (22 / 683);
		const artY = plate.y + target.h * (22 / 515);
		const scale = (target.w * (635 / 683)) / 682; // card-art width -> tile-art width

		gsap.timeline()
			.to(cardEl, { x: artX - 48 * scale, y: artY - 148 * scale, duration: 0.35, ease: 'power2.inOut' }, 0)
			.to(cardEl.scale, { x: scale, y: scale, duration: 0.35, ease: 'power2.inOut' }, 0)
			.call(finish) // commit: the real tile mounts underneath, art aligned
			.to(cardEl, { alpha: 0, duration: 0.2, ease: 'none', delay: 0.05 })
			.call(() => {
				// The commit's reconcile rebuilt any cell a duplicate copy kept
				// (hand.js self-heal) — this flown render is done either way.
				if (!cardEl.destroyed) cardEl.destroy({ children: true });
			});
	} catch (error) {
		console.error('playCardFlight failed (rendering state directly):', error);
		finish();
	}
}

/**
 * A CHARGE creature (server trigger "Charge" — Plump Pouncer &c.): it attacks
 * the instant it's played and, if a Ravenger, DEVOURS what it kills. The engine
 * reports the play in history.action and the auto-attack in history.charge, so
 * one timeline plays all three beats against the pre-play scene:
 *   1) FLIGHT — the card leaves the hand (a card-back for the foe) and lands on
 *      its board slot;
 *   2) LUNGE  — it darts at the charge target (a lane creature, or off the edge
 *      at the enemy leader), landing the impact;
 *   3) HOME   — it returns to its slot as the committed tile mounts underneath
 *      (a devoured victim shrinks away first, exactly like the attack lunge).
 * Spots in the action strings are the ACTOR's perspective; cellAtSpot mirrors
 * the rows when the OPPONENT charged (their tick is live in our state).
 * @param {string} cardSpot     "Card_<i>" (our hand slot; ignored for the foe)
 * @param {string} boardSpot    "Board_<y>_<x>" where the creature lands
 * @param {string} attackAction "Attack__Board_y_x__<target>"
 * @param {() => void} done
 */
function playChargeCreature(cardSpot, boardSpot, attackAction, done) {
	let finished = false;
	const finish = () => {
		if (!finished) {
			finished = true;
			done();
		}
	};
	try {
		const duel = store.duel;
		const flip = !!duel?.player2?.tick; // the OPPONENT charged -> mirror rows
		const landCell = cellAtSpot(boardSpot, flip);
		if (!landCell || !scene) {
			finish();
			return;
		}
		setTimeout(finish, 2600); // whatever the tweens do, never stall the game

		const parts = String(attackAction).split('__');
		const targetSpot = parts[2] || '';
		const faceHit = targetSpot.includes('Opponent');
		const victimCell = faceHit ? null : cellAtSpot(targetSpot, flip);
		// The leader taking a face hit, in OUR view: the enemy portrait when we
		// charge, ours when they do.
		const facePortrait = scene.barRefs?.portraits?.[flip ? 'Yourself' : 'Opponent'];

		// The charger sits on the board in the PENDING state — read its attack
		// there for the damage number.
		const pendingAttacker = duelQueue[0]?.board?.[landCell.y]?.[landCell.x];
		const damage = Math.max(0, Number(pendingAttacker?.attack || 0) + Number(pendingAttacker?.static?.extraAttack || 0));

		// A Ravenger DEVOURS its kill: the victim is present now but gone in the
		// pending state (matches playAttackLunge's ravenous test).
		const pendingVictim = victimCell && duelQueue[0]?.board?.[victimCell.y]?.[victimCell.x];
		const devoured = !!victimCell && (!pendingVictim || pendingVictim.owner !== victimCell.data?.owner);

		// Tile centers (fx/board layers share the origin at rest).
		const slotX = landCell.cellX + landCell.w / 2;
		const slotY = landCell.cellY + landCell.h / 2;
		let toX = slotX;
		let toY = slotY;
		if (victimCell) {
			toX = victimCell.cellX + victimCell.w / 2;
			toY = victimCell.cellY + victimCell.h / 2;
		} else {
			// Face hit: dart off the board toward the attacked leader's edge.
			toY = flip ? landCell.cellY + landCell.h * 1.5 : landCell.cellY - landCell.h * 0.9;
		}

		const impact = () => {
			if (victimCell) {
				flashTile(victimCell);
				shakeObject(victimCell.wrap);
				floatDamage(victimCell.cellX + victimCell.w / 2, victimCell.cellY + victimCell.h * 0.3, damage);
				if (devoured) shrinkTileAway(victimCell, 0.24);
			} else if (facePortrait && !facePortrait.container.destroyed) {
				// Face hit: shake only — the deck chip's red "−N" is the damage
				// readout (bars.js announceDeckChange), same as the attack lunge.
				shakeObject(facePortrait.container, 6);
			}
			if (damage > 4) screenShake(damage);
		};

		// Drive the shared 3-beat timeline for whatever sprite we fly. The fade +
		// destroy ride INSIDE this one timeline (like playCardFlight) — a separate
		// tween could be fast-forwarded onto the sprite after it's freed.
		const run = (sprite, flightScale) => {
			gsap.timeline({ onComplete: () => { finish(); if (sprite && !sprite.destroyed) sprite.destroy({ children: true }); } })
				// 1) FLIGHT: hand / portrait -> the creature's slot.
				.to(sprite, { x: slotX, y: slotY, duration: 0.34, ease: 'power2.out' }, 0)
				.to(sprite.scale, { x: flightScale, y: flightScale, duration: 0.34, ease: 'power2.out' }, 0)
				.to({}, { duration: 0.08 }) // planted, a beat
				// 2) LUNGE: slot -> target, then land the impact.
				.to(sprite, { x: toX, y: toY, duration: 0.26, ease: 'power2.in' })
				.call(impact)
				.to({}, { duration: devoured ? 0.3 : 0.06 }) // a beat to chew / recoil
				// 3) HOME: back to the slot; commit mounts the real tile underneath,
				// then the flown sprite crossfades away to reveal it.
				.to(sprite, { x: slotX, y: slotY, duration: 0.28, ease: 'power2.out' })
				.call(() => {
					// Stop any lingering shake/shrink on the victim BEFORE the commit
					// frees its wrap, so a fast-forwarded tween can't render onto a
					// destroyed PIXI object.
					if (victimCell?.wrap && !victimCell.wrap.destroyed) {
						gsap.killTweensOf(victimCell.wrap);
						gsap.killTweensOf(victimCell.wrap.scale);
					}
					finish();
				})
				.to(sprite, { alpha: 0, duration: 0.2, ease: 'none', delay: 0.02 });
		};

		const cardScale = (scene.metrics.tileH * 1.32) / 1082; // land ~tile-sized

		if (!flip && String(cardSpot).startsWith('Card_')) {
			// OUR play: fly the REAL hand card (center-pivoted like an event so it
			// scales/lunges around its middle).
			const sprite = detachHandCardRender(Number(cardSpot.split('_')[1]));
			if (!sprite) {
				finish();
				return;
			}
			const s = sprite.scale.x;
			sprite.pivot.set(778 / 2, 1082 / 2);
			sprite.x += (778 / 2) * s;
			sprite.y += (1082 / 2) * s;
			run(sprite, cardScale);
		} else {
			// The OPPONENT charged (hidden hand): a CARD BACK leaps from their
			// portrait to the slot, then lunges.
			PIXI.Assets.load('/cardframes2/Cardback.png').then((texture) => {
				if (!scene || !scene.boardCells) {
					finish();
					return;
				}
				const back = new PIXI.Sprite(texture);
				back.anchor.set(0.5);
				const backScale = (scene.metrics.tileH * 1.32) / texture.height;
				back.scale.set(backScale);
				const portrait = scene.barRefs?.portraits?.[flip ? 'Opponent' : 'Yourself'];
				if (portrait && !portrait.container.destroyed) {
					const g = portrait.container.getGlobalPosition();
					back.x = g.x + portrait.w / 2;
					back.y = g.y + portrait.h / 2;
				} else {
					back.x = scene.metrics.width / 2;
					back.y = -texture.height * backScale;
				}
				scene.layers.fx.addChild(back);
				run(back, backScale);
			}).catch(() => finish());
		}
	} catch (error) {
		console.error('playChargeCreature failed (rendering state directly):', error);
		finish();
	}
}

/**
 * The opponent played a creature/egg: their hand is hidden, so a CARD BACK
 * launches from their portrait, sails to the (mirrored) board slot, and
 * fades as the commit mounts the real tile underneath — the play is SEEN
 * instead of the tile just popping into existence.
 * @param {string} targetSpot  "Board_y_x" in the ACTOR's perspective.
 * @param {() => void} finish  Double-fire-safe completion from the caller.
 */
function playEnemyCardFly(targetSpot, finish) {
	try {
		const parts = String(targetSpot).split('_');
		const y = 3 - Number(parts[1]); // mirror to our view
		const x = Number(parts[2]);
		const target = scene?.boardCells?.[y]?.[x];
		if (!target) {
			finish();
			return;
		}
		setTimeout(finish, 1600); // never stall the game on a slow texture
		PIXI.Assets.load('/cardframes2/Cardback.png').then((texture) => {
			if (!scene || !scene.boardCells) {
				finish();
				return;
			}
			const back = new PIXI.Sprite(texture);
			back.anchor.set(0.5);
			back.scale.set((scene.metrics.tileH * 1.5) / texture.height);
			// Launch from their portrait — that's where their "hand" lives.
			const portrait = scene.barRefs?.portraits?.Opponent;
			if (portrait && !portrait.container.destroyed) {
				const g = portrait.container.getGlobalPosition();
				back.x = g.x + portrait.w / 2;
				back.y = g.y + portrait.h / 2;
			} else {
				back.x = scene.metrics.width / 2;
				back.y = -texture.height * back.scale.y;
			}
			back.rotation = -0.18;
			scene.layers.fx.addChild(back);
			const landScale = (target.h * 1.05) / texture.height;
			gsap.timeline()
				.to(back, {
					x: target.cellX + target.w / 2,
					y: target.cellY + target.h / 2,
					rotation: 0,
					duration: 0.4,
					ease: 'power2.inOut',
				}, 0)
				.to(back.scale, { x: landScale, y: landScale, duration: 0.4, ease: 'power2.inOut' }, 0)
				.call(finish) // commit: the real tile mounts underneath the back
				.to(back, { alpha: 0, duration: 0.22, ease: 'none', delay: 0.05 })
				.call(() => {
					if (!back.destroyed) back.destroy();
				});
		}).catch(() => finish());
	} catch (error) {
		console.error('playEnemyCardFly failed (rendering state directly):', error);
		finish();
	}
}

/**
 * Resolve an action spot to the live tile container, mirroring rows when the
 * action string came from the other player's perspective.
 * @param {string} spot  "Board_y_x"
 * @param {boolean} flip
 * @returns {any|null}
 */
function tileAtSpot(spot, flip) {
	const parts = String(spot).split('_');
	if (parts[0] !== 'Board') return null;
	let y = Number(parts[1]);
	const x = Number(parts[2]);
	if (flip) y = 3 - y;
	return scene?.tiles?.[y]?.[x] || null;
}

/**
 * Same as tileAtSpot but returns the CELL record (geometry + data + wrap).
 * @param {string} spot  "Board_y_x"
 * @param {boolean} flip
 * @returns {any|null}
 */
function cellAtSpot(spot, flip) {
	const parts = String(spot).split('_');
	if (parts[0] !== 'Board') return null;
	let y = Number(parts[1]);
	const x = Number(parts[2]);
	if (flip) y = 3 - y;
	return scene?.boardCells?.[y]?.[x] || null;
}
